<template>
  <div class="trend-card trend-card--leaderboard">
    <div class="trend-card__header trend-card__header--tight">
      <div class="trend-card__title">
        <i class="fa-duotone fa-trophy-star"></i>
        Leaderboard
      </div>
      <div class="trend-card__meta">{{ leaderboardRangeLabel }}</div>
    </div>

    <div class="trend-card__body trend-card__body--leaderboard">
      <div class="trend-loading" v-if="loading">Loading...</div>
      <div class="trend-empty" v-else-if="!leaderboard.length">No data</div>

      <div class="leaderboard" v-else>
        <div v-for="uw in leaderboard" :key="uw.underwriterAzureId" class="leaderboard__row">
          <div class="leaderboard__rank">
            <span class="rank-pill" :class="{ 'rank-pill--top': uw.leaderboardRank <= 3 }">
              {{ uw.leaderboardRank }}
            </span>
          </div>

          <div class="leaderboard__avatar">
            <div class="uw-avatar" :class="{ 'uw-avatar--loading': isPhotoLoading(uw.underwriterAzureId) }">
              <img
                v-if="photoUrl(uw.underwriterAzureId)"
                :src="photoUrl(uw.underwriterAzureId)"
                alt=""
                class="uw-avatar__img"
                @error="onUwImgError(uw.underwriterAzureId)"
              />
              <div v-else class="uw-avatar__fallback">
                {{ initials(uw.underwriterDisplayName) }}
              </div>
            </div>
          </div>

          <div class="leaderboard__name">
            <div class="leaderboard__name-line">{{ uw.underwriterDisplayName || "—" }}</div>
            <div class="leaderboard__sub">
              {{ cleanJobTitle(uw.underwriterJobTitle) || "—" }}
            </div>
          </div>

          <div class="leaderboard__avgdays-col">
            <div
              v-if="uw.avgInitialUwLoansPerActiveDay != null"
              class="leaderboard__avgdays"
              :title="`Avg Initial UW Loans/Active Day: ${formatDays(uw.avgInitialUwLoansPerActiveDay)}`"
            >
              <i class="fa-duotone fa-gauge-high"></i>
              <span class="leaderboard__avgdays-value">{{ formatDays(uw.avgInitialUwLoansPerActiveDay) }}</span>
              <span class="leaderboard__avgdays-label">avg/day</span>
            </div>
            <div v-else class="leaderboard__avgdays-empty">—</div>
          </div>

          <div class="leaderboard__value">
            <div class="leaderboard__value-main">{{ Number(uw.totalDistinctLoans || 0) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="leaderboard-footer" v-if="leaderboard.length">
      <div class="footer-pill">
        <span class="footer-label">Total Loans</span>
        <span class="footer-value">{{ totalInitialDistinctLoans }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"
import auth from "@/authConfig"

import {
  readCachedPhoto,
  writeCachedPhoto,
  writeCachedNotFound,
  normalizePhotoSrc
} from "@/profilePhotoCache.js"

export default {
  name: "InitialUwLeaderboardCard",
  props: {
    azureId: { type: String, required: true },
    authToken: { type: String, required: true },
    orgId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,
      leaderboard: [],
      dailyAggTotals: [],

      uwPhotos: {},
      uwLoading: {},
      uwFailed: {},
      uwCacheTtlMs: 1000 * 60 * 60 * 24 * 7
    }
  },
  computed: {
    leaderboardRange() {
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      const start = new Date(end)
      start.setDate(start.getDate() - 89)
      start.setHours(0, 0, 0, 0)
      return { start, end }
    },
    leaderboardRangeLabel() {
      const { start, end } = this.leaderboardRange
      return `Rolling 90 days • ${this.shortDate(start)} - ${this.shortDate(end)}`
    },
    totalInitialDistinctLoans() {
      return this.dailyAggTotals.reduce((a, x) => a + Number(x.initialDistinctLoans || 0), 0)
    }
  },
  methods: {
    shortDate(d) {
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      const yyyy = d.getFullYear()
      return `${mm}/${dd}/${yyyy}`
    },
    async fetchLeaderboard() {
      if (!this.azureId || !this.authToken) return
      this.loading = true
      try {
        const { start, end } = this.leaderboardRange
        const resp = await ApiService.GetUnderwrittenLoanStatsByUnderwriter(
          this.azureId,
          start,
          end,
          this.authToken,
          this.orgId
        )
        this.normalize(resp?.data || {})
      } finally {
        this.loading = false
      }
    },
    normalize(payload) {
      const data = payload?.data?.data || payload?.data || payload || {}
      const lb = Array.isArray(data.leaderboard) ? data.leaderboard : []
      const dailyTrend = Array.isArray(data.dailyTrend) ? data.dailyTrend : []

      this.leaderboard = [...lb]
        .map(x => ({
          underwriterAzureId: x.underwriterAzureId,
          underwriterDisplayName: x.underwriterDisplayName,
          underwriterEmail: x.underwriterEmail,
          underwriterJobTitle: x.underwriterJobTitle,
          totalDistinctLoans: Number(x.totalDistinctLoans || 0),
          activeDays: Number(x.activeDays || 0),
          avgInitialUwLoansPerActiveDay:
            x.avgInitialUwLoansPerActiveDay != null ? Number(x.avgInitialUwLoansPerActiveDay) : null,
          leaderboardRank: Number(x.leaderboardRank || 9999)
        }))
        .sort((a, b) => a.leaderboardRank - b.leaderboardRank)
        .slice(0, 99)

      const rows = [...dailyTrend]
        .map(x => ({
          dateValue: x.initialUwDate,
          initialDistinctLoans: Number(x.initialDistinctLoans || 0)
        }))
        .filter(x => x.dateValue)

      const dates = [...new Set(rows.map(r => this.dateKeyLocal(r.dateValue)))].sort()
      this.dailyAggTotals = dates.map(d => {
        let sumLoans = 0
        for (const r of rows) {
          if (this.dateKeyLocal(r.dateValue) === d) sumLoans += Number(r.initialDistinctLoans || 0)
        }
        return { dateValue: d, initialDistinctLoans: sumLoans }
      })
    },
    dateKeyLocal(v) {
      const d = new Date(v)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      return `${yyyy}-${mm}-${dd}`
    },
    cleanJobTitle(v) {
      return String(v || "")
        .replace(/\([^)]*\)/g, "")
        .replace(/\s{2,}/g, " ")
        .trim()
    },
    formatDays(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return "—"
      return `${Math.round(n * 100) / 100}`
    },
    initials(name) {
      const s = String(name || "").trim()
      if (!s) return "—"
      const parts = s.split(/\s+/).filter(Boolean)
      if (!parts.length) return "—"
      const a = parts[0]?.[0] || ""
      const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : ""
      return (a + b).toUpperCase()
    },
    photoUrl(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return ""
      if (!this.uwPhotos[id] && !this.uwLoading[id] && !this.uwFailed[id]) {
        this.ensurePhotoLoaded(id)
      }
      return this.uwPhotos[id] || ""
    },
    isPhotoLoading(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return false
      return !!this.uwLoading[id]
    },
    async ensurePhotoLoaded(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return
      if (this.uwPhotos[id]) return
      if (this.uwLoading[id]) return
      if (this.uwFailed[id]) return

      this.uwLoading = { ...this.uwLoading, [id]: true }

      try {
        const cached = await readCachedPhoto(id, this.uwCacheTtlMs)
        if (cached === "__NOT_FOUND__") {
          this.uwFailed = { ...this.uwFailed, [id]: true }
          return
        }

        if (cached) {
          const normalized = normalizePhotoSrc(cached)
          this.uwPhotos = { ...this.uwPhotos, [id]: normalized }
          return
        }

        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse?.accessToken || ""
        if (!token) return

        const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(id)}/photo/$value`
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })

        if (!resp.ok) {
          this.uwFailed = { ...this.uwFailed, [id]: true }
          await writeCachedNotFound(id)
          return
        }

        const blob = await resp.blob()

        const base64 = await new Promise(resolve => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(blob)
        })

        const normalized = normalizePhotoSrc(base64)
        this.uwPhotos = { ...this.uwPhotos, [id]: normalized }
        await writeCachedPhoto(id, normalized)
      } catch {
        this.uwFailed = { ...this.uwFailed, [id]: true }
        await writeCachedNotFound(id)
      } finally {
        this.uwLoading = { ...this.uwLoading, [id]: false }
      }
    },
    async onUwImgError(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return
      this.uwPhotos = { ...this.uwPhotos, [id]: "" }
      this.uwFailed = { ...this.uwFailed, [id]: true }
      await writeCachedNotFound(id)
    }
  },
  mounted() {
    this.fetchLeaderboard()
  },
  watch: {
    azureId() {
      this.fetchLeaderboard()
    },
    authToken() {
      this.fetchLeaderboard()
    },
    orgId() {
      this.fetchLeaderboard()
    }
  }
}
</script>

<style scoped>
.trend-card {
  background: var(--background);
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.trend-card--leaderboard {
  position: sticky;
  top: 12px;
  max-height: calc(100vh - 94px);
  display: flex;
  flex-direction: column;
}

.trend-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.02);
}

.trend-card__header--tight {
  padding: 9px 10px;
}

.trend-card__title {
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary-on-background);
}

.trend-card__meta {
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.trend-card--leaderboard .trend-card__title i.fa-duotone {
  --fa-primary-color: rgba(255, 168, 0, 0.95);
  --fa-secondary-color: rgba(255, 168, 0, 0.32);
}

.trend-card__body--leaderboard {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px 10px;
  min-height: 0;
}

.trend-loading,
.trend-empty {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  gap: 10px;
}

.leaderboard {
  flex: 1;
  overflow: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
  min-height: 0;
}

.leaderboard__row {
  display: grid;
  grid-template-columns: 28px 34px 1fr 120px auto;
  gap: 8px;
  align-items: center;
  padding: 7px 7px;
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  background: rgba(5, 29, 58, 0.02);
}

.uw-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid var(--border-ltr);
  background: rgba(40, 110, 255, 0.08);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
}

.uw-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.uw-avatar__fallback {
  font-weight: 950;
  font-size: 11px;
  color: var(--text-primary-on-background);
}

.rank-pill {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-weight: 950;
  font-size: 11px;
  border: 1px solid var(--border-lt);
  background: var(--button);
  color: var(--on-button);
}

.rank-pill--top {
  border-color: rgba(40, 110, 255, 0.28);
  background: rgba(40, 110, 255, 0.12);
  color: var(--text-primary-on-background);
}

.leaderboard__name-line {
  font-weight: 950;
  font-size: 12.5px;
  color: var(--text-primary-on-background);
}

.leaderboard__sub {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leaderboard__avgdays {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 10px;
  border: 1px solid rgba(40, 110, 255, 0.28);
  background: rgba(40, 110, 255, 0.10);
  min-width: 120px;
  box-shadow: 0 8px 18px rgba(40, 110, 255, 0.12);
}

.leaderboard__avgdays-empty {
  font-weight: 900;
  font-size: 11px;
  color: var(--text-secondary-on-background);
  opacity: 0.75;
}

.leaderboard__avgdays-value {
  font-weight: 950;
  font-size: 12px;
}

.leaderboard__avgdays-label {
  font-weight: 900;
  font-size: 10.5px;
  color: var(--text-secondary-on-background);
}

.leaderboard__value {
  text-align: right;
  min-width: 54px;
}

.leaderboard__value-main {
  font-weight: 950;
  font-size: 13px;
  color: var(--text-primary-on-background);
}

.leaderboard-footer {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 8px 10px 8px;
  border-top: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.02);
}

.footer-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-ltr);
  background: var(--background);
}

.footer-label {
  font-size: 10.5px;
  font-weight: 950;
  color: var(--text-secondary-on-background);
}

.footer-value {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
}

@media (max-width: 1200px) {
  .trend-card--leaderboard {
    position: static;
    max-height: none;
  }

  .leaderboard {
    overflow: visible;
  }
}
</style>
